import { useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '@core/hooks/redux-hooks';
import { selectSortedCasesStructure } from '@core/store/project-slice-selectors';
import { cloneToMutableData, enumKeyByValue, sumBy } from '@app/shared/helpers';
import { SubmitHandler, useForm } from 'react-hook-form';
import { formConfigBase } from '@app/shared/constants/form-config-base';
import { updateProjectDraft } from '@core/store/project-slice';
import { CaseDto, DiscountRateOverrideDto } from '@app/shared/models/contracts/project-dto';
import { selectInstruments } from '@app/core/store/capital-structure-slice-selectors';
import useTriggerEffect from '@app/core/hooks/useTriggerEffect';
import useInstrumentSort from '@app/shared/hooks/useInstrumentSort';
import { InstrumentType, TrancheKey } from '@app/shared/models/contracts/enums/shared-enums';

export type OverrideField = keyof Omit<DiscountRateOverrideDto, 'instrumentId'>;

const useDiscountRateOverride = () => {
  const projectDraft = useAppSelector((state) => state.project.projectDraft);
  const casesStructure = useAppSelector(selectSortedCasesStructure);
  const dispatch = useAppDispatch();
  const instruments = useAppSelector(selectInstruments);
  const valuedInstruments = useMemo(
    () => instruments.filter((instrument) => instrument.shouldBeValued),
    [instruments]
  );
  const sortedValuedInstruments = useInstrumentSort(valuedInstruments);
  const columnsCount =
    sumBy(projectDraft.pwermInput.cases, (caseItem) => caseItem.forecasts.length) + 1;

  const initializedCases = useMemo(() => {
    return projectDraft.pwermInput.cases.map((caseItem) => ({
      ...caseItem,
      forecasts: caseItem.forecasts.map((forecast) => {
        const existingOverrides = cloneToMutableData(forecast.discountRateOverrides ?? []);

        sortedValuedInstruments.forEach((instrument) => {
          const isOE =
            instrument.type === enumKeyByValue(InstrumentType, InstrumentType.OrdinaryEquity);
          const hasTranches = isOE && Boolean(instrument.payoutLogic?.length);

          let instrumentEntry = existingOverrides.find(
            (o) => o.instrumentId === instrument.instrumentId
          );

          if (!instrumentEntry) {
            instrumentEntry = {
              instrumentId: instrument.instrumentId,
              infusionDiscountRateOverride: null,
              otherInterimsDiscountRateOverride: null,
              exitDistributionByTrancheDiscountRateOverrides: {},
            };
            existingOverrides.push(instrumentEntry);
          }

          const exitMap = instrumentEntry.exitDistributionByTrancheDiscountRateOverrides;
          if (hasTranches) {
            instrument.payoutLogic?.forEach((tranche) => {
              if (!(tranche.id in exitMap)) {
                exitMap[tranche.id] = null;
              }
            });
          } else {
            if (!(TrancheKey.DefaultTrancheKey in exitMap)) {
              exitMap[TrancheKey.DefaultTrancheKey] = null;
            }
          }
        });

        return {
          ...forecast,
          discountRateOverrides: existingOverrides,
        };
      }),
    }));
  }, [projectDraft.pwermInput.cases, sortedValuedInstruments]) as CaseDto[];

  const formMethods = useForm({
    ...formConfigBase,
    defaultValues: {
      cases: initializedCases,
    },
  });

  const { trigger, getValues } = formMethods;

  useTriggerEffect(trigger);

  const formSubmitHandler = () => {
    const formValues = getValues();
    const formCases = cloneToMutableData(formValues.cases);

    const updatedProjectDraft = {
      ...projectDraft,
      pwermInput: {
        ...projectDraft.pwermInput,
        cases: formCases.map((caseItem) => ({
          ...caseItem,
          forecasts: caseItem.forecasts.map((forecast) => ({
            ...forecast,
            discountRateOverrides: forecast.discountRateOverrides,
          })),
        })),
      },
    };

    dispatch(updateProjectDraft({ project: updatedProjectDraft }));
  };

  const findSortedCaseIndex = (caseId: string) => {
    return projectDraft.pwermInput.cases.findIndex((item) => item.caseId === caseId);
  };

  const handleRevertCategory = (
    instrumentId: string,
    field: OverrideField,
    trancheId?: Nullable<string>
  ) => {
    const casesClone = cloneToMutableData(getValues().cases);

    casesClone.forEach((caseItem) => {
      caseItem.forecasts.forEach((forecast) => {
        const overrides = forecast.discountRateOverrides ?? [];
        const instrumentEntry = overrides.find((o) => o.instrumentId === instrumentId);
        if (!instrumentEntry) {
          return;
        }
        if (field === 'exitDistributionByTrancheDiscountRateOverrides') {
          const exitMap = instrumentEntry.exitDistributionByTrancheDiscountRateOverrides;
          if (trancheId !== undefined && trancheId !== null) {
            exitMap[trancheId] = null;
          } else {
            Object.keys(exitMap).forEach((key) => {
              exitMap[key] = null;
            });
          }
        } else {
          instrumentEntry[field] = null;
        }
      });
    });

    const updatedProjectDraft = {
      ...projectDraft,
      pwermInput: {
        ...projectDraft.pwermInput,
        cases: casesClone,
      },
    };

    dispatch(updateProjectDraft({ project: updatedProjectDraft }));
    formMethods.reset({ cases: casesClone });
  };

  return {
    formMethods,
    formSubmitHandler: formSubmitHandler as SubmitHandler<{ cases: CaseDto[] }>,
    initializedCases,
    casesStructure,
    sortedValuedInstruments,
    columnsCount,
    handleRevertCategory,
    findSortedCaseIndex,
  };
};

export default useDiscountRateOverride;
